import { useQuery } from "@apollo/client";
import { Link, useNavigate, useParams } from "react-router-dom";
import { GET_COMPANY_DETAILS } from "../../graphql/companyQueries";
import { useEffect, useState, useRef } from "react";
import Spinner from "../Spinner";
import { formatDate } from "../../utils/dateFormat";
import { UserOutlined, LockOutlined, DownOutlined, LogoutOutlined } from "@ant-design/icons";
import NotificationBell from "../NotificationBell";
import SidebarToggle from "../Sidebar/SidebarToggle";

const Header = () => {
  const { companyId } = useParams();
  const [company, setCompany] = useState({});
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const user = JSON.parse(localStorage.getItem("userInfo"));
  const navigate = useNavigate();
  const isAgentRole = user?.role?.name === "AGENT";

  const { error, loading, data } = useQuery(GET_COMPANY_DETAILS, {
    variables: { companyId },
    skip: !user,
  });

  useEffect(() => {
    if (data) setCompany(data.getCompanyById);
  }, [data]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // If no user, don't render header (user is logging out)
  if (!user) return null;

  if (loading) return <Spinner />;

  const handleMenuClick = (path) => {
    setIsDropdownOpen(false);
    navigate(path);
  };

  const handleLogout = () => {
    setIsDropdownOpen(false);
    localStorage.removeItem("userToken");
    localStorage.removeItem("userInfo");
    navigate("/");
  };

  return (
    <header className="flex w-full text-secondary items-center pt-4 mb-2 justify-between">
      <div className="flex items-center gap-3">
        <SidebarToggle />
        {user?.role?.name === "SUPER_ADMIN" ? (
          <Link
            onClick={() => navigate("/administrator/dashboard")}
            className="inline-flex bg-primary p-2 text-white rounded font-semibold hover:opacity-90"
          >
            <span className="mx-auto">{`<<< Super Admin Dashboard`}</span>
          </Link>
        ) : null}
      </div>
      <div className="flex items-center gap-4">
        <div className="text-2xl text-secondary text-end">
          <div>Welcome back, {isAgentRole ? `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || company.companyName : company.companyName}!</div>
          <div className="text-xs py">
            FyrstUp user since: {formatDate(company.createdAt)}
          </div>
        </div>
        {/* Notification Bell — shown for all company-context roles */}
        <NotificationBell />
        {/* User Dropdown Menu — agent only */}
        {isAgentRole && (
          <>
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center gap-1 p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                <UserOutlined className="text-xl text-secondary" />
                <DownOutlined className="text-xs text-secondary" />
              </button>
            {isDropdownOpen && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-50">
                <div className="py-1">
                  <button
                    onClick={() => handleMenuClick(`/company/${companyId}/agent/user-details`)}
                    className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <UserOutlined />
                    User Details
                  </button>
                  <button
                    onClick={() => handleMenuClick(`/company/${companyId}/agent/reset-password`)}
                    className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <LockOutlined />
                    Reset Password
                  </button>
                  <div className="border-t border-gray-200"></div>
                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-2 w-full px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                  >
                    <LogoutOutlined />
                    Logout
                  </button>
                </div>
              </div>
            )}
            </div>
          </>
        )}
      </div>
    </header>
  );
};

export default Header;
